// Test & Refine redesign - shell.
//
// Presentational root for the flag-gated redesign. It owns NO data state:
// every service call, orchestration handler, and data prop is passed in
// from AISandboxPage (the orchestrator keeps its contract). The shell only
// holds view-only UI state (which tab, and delegates the rest to the views)
// and routes between Configure / Results / History.
import { FlaskConical, Play } from "lucide-react";
import { ModuleHeader } from "../../shell/module-header";
import "./test-refine-redesign.css";
import { TrOrb } from "./shared";

import type { AIModel } from "@/app/constants/models";
import type {
  SimulationQueueItem,
  MonitoringBeforeData,
  AISandboxData,
  SandboxSuggestion,
} from "@/app/services/simulation/ai-sandbox-service";

import { ConfigureView } from "./configure-view";
import { RunningView } from "./running-view";
import { ResultsView } from "./results-view";
import { HistoryView } from "./history-view";

export type MonitoringSource = "live" | "cached" | "none";

/** An optional AI-authored proposal. Absent until a real backend source
 *  supplies it; when absent, the proposal band stays hidden (never
 *  fabricated). Kept here so the wiring seam is ready for Gagan. */
export interface TestRefineProposal {
  query: string;
  beforePct: number;
  predictedPct: number;
  targetActionIds: number[];
  themeLabel: string;
  currentRank: number;
}

export interface TestRefineShellProps {
  brand: string;
  sandboxData: AISandboxData;
  monitoringData: MonitoringBeforeData | null;
  monitoringSource: MonitoringSource;

  availableModels: AIModel[];
  selectedModels: string[];
  setSelectedModels: (ids: string[]) => void;
  onModelChangeContext?: (id: string, added: boolean, count: number) => void;

  query: string;
  setQuery: (q: string) => void;

  activeTab: string;
  setActiveTab: (t: string) => void;

  isSimulating: boolean;
  hasRunSimulation: boolean;

  competitors: string[];
  competitorInput: string;
  setCompetitorInput: (v: string) => void;
  addCompetitor: () => void;
  removeCompetitor: (name: string) => void;

  selectedQueueItems: number[];
  setSelectedQueueItems: (ids: number[]) => void;

  selectedFile: File | null;
  setSelectedFile: (f: File | null) => void;

  onViewContent: (item: SimulationQueueItem) => void;

  appliedQueueItems: SimulationQueueItem[];
  onRunSimulation: () => void;
  onHistoryClick: (runId: number) => void;

  /** AI-proposed complete tests (question + actions + why) + loading state. */
  suggestions: SandboxSuggestion[];
  suggestionsLoading: boolean;

  /** Opens the AI assistant, optionally with a topic prefilled. */
  onOpenAIChat: (topic?: string) => void;

  /** Optional AI proposal. When undefined the band is hidden. */
  proposal?: TestRefineProposal;
}

const TABS: Array<{ value: string; label: string }> = [
  { value: "configure", label: "Configure" },
  { value: "results", label: "Results" },
  { value: "history", label: "History" },
];

export function TestRefineShell(props: TestRefineShellProps) {
  const {
    brand,
    sandboxData,
    activeTab,
    setActiveTab,
    selectedModels,
  } = props;

  const history = sandboxData.simulationHistory || [];

  const modelSelCount = selectedModels.length;

  // Horizontal gutter is handled by the app-standard px-4 md:px-8 wrapper
  // below (matches the rest of the app), so sections carry vertical padding
  // only. Full width, no centered max-width column.
  const PADDING = "0px";

  return (
    <div className="tr-root" style={{ background: "var(--background)", color: "var(--foreground)" }}>
      <ModuleHeader
        page="Test & Refine"
        icon={FlaskConical}
        title="Test changes before you make them."
        description={`Run a private simulation to see how AI would describe and recommend ${brand || "your brand"} if you shipped your queued actions. Nothing goes live.`}
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <div className="w-full px-4 md:px-8">
        {/* Shared module header band (breadcrumb, title, stats, tabs) */}

        {/* ── Bodies ────────────────────────────────────────────── */}
        {/* Body sits in a second px-2 md:px-8, matching Brand Monitoring's
            nested body padding (header stays at the outer px-4 md:px-8). */}
        {activeTab === "configure" && (
          <div className="px-2 md:px-8">
            <ConfigureView {...props} padding={PADDING} />
          </div>
        )}

        {activeTab === "results" && (
          <div className="px-2 md:px-8" style={{ paddingTop: 24, paddingBottom: 60 }}>
            {props.isSimulating ? (
              <RunningView
                query={props.query}
                modelIds={selectedModels}
                actionsApplied={props.selectedQueueItems.length}
              />
            ) : props.hasRunSimulation ? (
              <ResultsView
                sandboxData={sandboxData}
                query={props.query}
                brand={brand}
                appliedQueueItems={props.appliedQueueItems}
                monitoringSource={props.monitoringSource}
                selectedModelCount={modelSelCount}
                onOpenAIChat={props.onOpenAIChat}
              />
            ) : (
              <EmptyResults onConfigure={() => setActiveTab("configure")} />
            )}
          </div>
        )}

        {activeTab === "history" && (
          <div className="px-2 md:px-8" style={{ paddingTop: 24, paddingBottom: 60 }}>
            <HistoryView history={history} onSelectRun={props.onHistoryClick} />
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyResults({ onConfigure }: { onConfigure: () => void }) {
  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 14,
        padding: "56px 24px",
        textAlign: "center",
        background: "var(--muted)",
      }}
    >
      <div style={{ display: "inline-flex", marginBottom: 14 }}>
        <TrOrb size={44} radius={12} iconSize={22} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--foreground)", marginBottom: 5 }}>
        No simulation yet
      </div>
      <p
        style={{
          fontSize: 13.5,
          color: "var(--muted-foreground)",
          margin: "0 auto 16px",
          maxWidth: 380,
        }}
      >
        Configure a query and actions, then run a simulation to see the before to after impact here.
      </p>
      <button
        onClick={onConfigure}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: 13,
          fontWeight: 600,
          color: "var(--on-teal-text)",
          background: "var(--alpha-teal)",
          border: "none",
          borderRadius: 9,
          padding: "9px 16px",
          cursor: "pointer",
        }}
      >
        <Play size={14} strokeWidth={1.8} />
        Go to Configure
      </button>
    </div>
  );
}
